import { Checkbox, CheckboxGroup, Input } from '@heroui/react';
import type { TypedUseQueryHookResult } from '@reduxjs/toolkit/query/react';
import { type FC, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { ClipLoader } from 'react-spinners';
import type { FormControlProps } from '../types/form-props.type.ts';
import type { LookupModel } from '../types/lookup.type.ts';

interface DietarySelectorProps extends FormControlProps {
  query: TypedUseQueryHookResult<LookupModel[], void, any>;
  otherName: string;
}

export const DietarySelector: FC<DietarySelectorProps> = ({ query, label, name, otherName }) => {
  const { control, setValue, getValues } = useFormContext();

  const [selectedValues, setSelectedValues] = useState<string[]>(() => {
    const currentDietaryValues = (getValues(name) ?? []) as LookupModel[];
    const initialSelectedIds = currentDietaryValues.map(v => v.id.toString());
    const initialOtherValue = getValues(otherName);
    return initialOtherValue ? [...initialSelectedIds, 'other'] : initialSelectedIds;
  });

  const [showOtherInput, setShowOtherInput] = useState(() => !!getValues(otherName));

  const handleValueChange = (values: string[]) => {
    setSelectedValues(values);

    const hasOther = values.includes('other');
    setShowOtherInput(hasOther);

    if (!hasOther) {
      setValue(otherName, '');
    }

    const selectedLookups = query.data?.filter(item => values.includes(item.id.toString())) ?? [];

    setValue(name, selectedLookups);
  };

  return (
    <div className='flex flex-col gap-3'>
      <CheckboxGroup
        label={label}
        value={selectedValues}
        onValueChange={handleValueChange}
        classNames={{ base: 'w-full' }}
      >
        {query.isLoading && <ClipLoader size={20} />}
        {query.data?.map(value => (
          <Checkbox
            key={value.id}
            value={value.id.toString()}
            color='primary'
            classNames={{
              base: 'ml-4',
              label: 'text-sm font-semibold',
            }}
          >
            {value.description}
          </Checkbox>
        ))}
        <Checkbox
          value='other'
          color='primary'
          classNames={{
            base: 'ml-4',
            label: 'text-sm font-semibold',
          }}
        >
          Other
        </Checkbox>
      </CheckboxGroup>
      {showOtherInput && (
        <Controller
          name={otherName}
          control={control}
          rules={{ required: showOtherInput ? 'This field is required' : undefined }}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              label='Other'
              variant='bordered'
              isRequired={showOtherInput}
              isDisabled={!showOtherInput}
              isInvalid={fieldState.invalid}
              errorMessage={fieldState.error?.message}
            />
          )}
        />
      )}
    </div>
  );
};
